import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import styled, {css} from 'styled-components/native';
import TextGeneric from '../../components/textGeneric';
import {RootState} from '../../redux/store';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  DateRangePickerModalProps,
  DateRangeType,
} from '../../interfaces/connected-components/dateRangePicker';
import DateTimePicker from 'react-native-ui-datepicker';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {closeDateRangePickerModal} from './actions';
import {tr} from '../../translations';
import {getFontFamily} from '../../utils/helpers';
import {Platform, TouchableOpacity} from 'react-native';
import Icon from '../../components/icon';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const isWeb = Platform.OS === 'web';

// Web-specific modal styles
const WebOverlayContainer = styled(Animated.View)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  z-index: 10002;
`;

const WebModalContainer = styled(Animated.View)<{width: string}>`
  width: ${({width}) => width};
  max-height: 90vh;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 12px;
  overflow: hidden;
  shadow-color: #000;
  shadow-offset: 0px 5px;
  shadow-opacity: 0.2;
  shadow-radius: 15px;
  elevation: 10;
  z-index: 2;
  position: relative;
  ${isWeb ? 'box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.15);' : ''}
`;

const WebHeaderRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom-width: 2px;
  border-bottom-color: ${({theme}) => theme.border + '33'};
  background-color: ${({theme}) => theme.homeBackground};
`;

const WebCloseButton = styled.TouchableOpacity`
  padding: 8px;
  border-radius: 20px;
  align-items: center;
  justify-content: center;
  ${isWeb &&
    css`
      transition: background-color 0.2s ease;
      &:hover {
        background-color: ${({theme}: {theme: Theme}) => theme.ripple};
      }
    `}
`;

// Mobile-specific styles remain unchanged
const Container = styled.View`
  width: 100%;
  height: 100%;
  overflow: hidden;
  align-self: center;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  position: absolute;
`;

const Overlay = styled(Animated.View)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
`;
const ContentContainer = styled(Animated.View)`
  align-items: center;
  border-radius: 16px;
  align-self: center;
  width: 85%;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.2;
  shadow-radius: 4px;
  elevation: 5;
  overflow: hidden;
  z-index: 10002;
  background-color: ${({theme}) => theme.homeBackground + 'FA'};
`;

const RowView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  align-self: center;
  border-top-width: 1px;
  border-color: ${({theme}) => theme.text.border + 'AA'};
  ${isWeb ? 'border-top-width: 2px;' : ''}
`;
const CustomButton = styled.TouchableOpacity<{isWeb?: boolean}>`
  flex: 1;
  align-items: center;
  align-self: center;
  padding: ${({isWeb}) => (isWeb ? '16px 20px' : '4%')};
  ${({disabled}) =>
    disabled &&
    `
    opacity:0.5;
    `};
  ${({isWeb}) =>
    isWeb &&
    css`
      transition: background-color 0.2s ease;
      &:hover {
        background-color: ${({theme}: {theme: Theme}) => theme.ripple + '40'};
      }
      &:active {
        background-color: ${({theme}: {theme: Theme}) => theme.ripple + '60'};
      }
    `}
`;
const Label = styled(TextGeneric)<{isGrey?: boolean}>`
  color: ${({theme, isGrey}) => (isGrey ? theme.text.grey : theme.primary)};
  font-size: ${({theme}) => theme.text.s8}px;
  text-align: center;
`;
const Separator = styled.View`
  height: 100%;
  width: 1px;
  background-color: ${({theme}) => theme.border + 'BB'};
`;
const DateTimePickerContainer = styled.View<{isWeb?: boolean}>`
  width: 100%;
  align-self: center;
  padding-top: ${({isWeb}) => (isWeb ? '24px' : '2%')};
  padding-bottom: ${({isWeb}) => (isWeb ? '16px' : '0')};
  padding-horizontal: ${({isWeb}) => (isWeb ? '20px' : '0')};
`;
const TitleContainer = styled.View`
  padding: 5%;
  width: 100%;
  background-color: ${({theme}) => theme.primary};
  align-items: center;
  justify-content: center;
`;
const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s8}px;
  letter-spacing: 0.5px;
  text-align: center;
`;

/**
 * A component that renders a date range picker modal
 *
 * @param {DateRangePickerModalProps} props - The properties passed to the component, including the date range picker modal's state.
 */
const DateRangePickerModal = ({
  dateRangePickerModal,
}: DateRangePickerModalProps) => {
  const theme = useTheme() as Theme;
  const {getByScreenSize} = useResponsiveDimensions();
  const {isVisible, onConfirm, mode, currentRange, datePickerProps} =
    dateRangePickerModal || {};
  const [modalVisible, setModalVisible] = useState(false);
  const animationController = useSharedValue(0);
  const [range, setRange] = React.useState<DateRangeType>({
    startDate: undefined,
    endDate: undefined,
    date: undefined,
  });
  useEffect(() => {
    animationController.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    if (isVisible) {
      if (currentRange) {
        setRange(currentRange);
      } else {
        setRange({startDate: undefined, endDate: undefined, date: undefined});
      }
      setModalVisible(true);
    } else {
      setTimeout(() => {
        setRange({
          startDate: new Date(),
          endDate: new Date(),
          date: new Date(),
        });
        setModalVisible(false);
      }, 300);
    }
  }, [isVisible]);

  const closeModal = (isConfirm: boolean = true) => {
    if (isConfirm) {
      onConfirm &&
        onConfirm(
          range?.date ? range : {date: datePickerProps?.maxDate || new Date()},
        );
    }
    closeDateRangePickerModal();
  };

  const overlayStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: `rgba(0, 0, 0, ${animationController.value * 0.5})`,
    };
  });

  const opacityModalStyle = useAnimatedStyle(() => {
    const opacityModal = interpolate(animationController.value, [0, 1], [0, 1]);
    const scale = interpolate(animationController.value, [0, 1], [0.8, 1]);
    return {
      opacity: opacityModal,
      transform: [{scale}],
    };
  });

  const renderDatePicker = () => (
    <DateTimePickerContainer isWeb={isWeb}>
      <DateTimePicker
        mode={mode || datePickerProps?.mode || 'single'}
        startDate={range?.startDate}
        endDate={range?.endDate}
        date={range?.date || datePickerProps?.maxDate || new Date()}
        onChange={(params: any) => {
          setRange(params);
        }}
        maxDate={datePickerProps?.maxDate || new Date()}
        enabledDates={datePickerProps?.enabledDates}
        disabledDates={datePickerProps?.disabledDates}
        minDate={datePickerProps?.minDate}
        styles={{
          selected: {
            backgroundColor: theme.primary,
            borderRadius: 8,
          },
          selected_label: {
            fontFamily: getFontFamily().bold,
            color: theme.text.light,
            letterSpacing: 1,
          },
          today_label: {
            color: theme.text.dark,
          },
          day_label: {
            color: theme.text.dark,
            fontFamily: getFontFamily().regular,
          },
          disabled_label: {
            color: theme.text.grey + '77',
            fontFamily: getFontFamily().regular,
          },
          weekday: {
            color: theme.text.dark,
            fontFamily: getFontFamily().semi_bold,
          },
          header: {
            color: theme.text.dark,
            fontFamily: getFontFamily().bold,
          },
        }}
      />
    </DateTimePickerContainer>
  );

  const renderActionButtons = () => (
    <RowView>
      <CustomButton isWeb={isWeb} onPress={() => closeModal(false)}>
        <Label isGrey bold>
          {tr('app.cancel')}
        </Label>
      </CustomButton>
      <Separator />
      <CustomButton isWeb={isWeb} onPress={() => closeModal()}>
        <Label bold>{tr('app.save')}</Label>
      </CustomButton>
    </RowView>
  );

  // Web-specific render
  if (isWeb && modalVisible) {
    return (
      <WebOverlayContainer style={overlayStyle}>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}
          onPress={() => closeModal(false)}
          activeOpacity={1}
        />
        <WebModalContainer
          width={getByScreenSize('95%', '520px')}
          style={opacityModalStyle}>
          <WebHeaderRow>
            <Title semiBold>
              {mode === 'range'
                ? tr('summaries.selectRange')
                : tr('summaries.selectDate')}
            </Title>
            <WebCloseButton onPress={() => closeModal(false)}>
              <Icon
                name={'close'}
                size={theme.text.s6}
                color={theme.text.dark}
              />
            </WebCloseButton>
          </WebHeaderRow>
          {renderDatePicker()}
          {renderActionButtons()}
        </WebModalContainer>
      </WebOverlayContainer>
    );
  }

  // Mobile render remains unchanged
  return (
    <Container pointerEvents={modalVisible ? 'auto' : 'none'}>
      <Overlay style={overlayStyle} onTouchStart={() => closeModal(false)} />
      <ContentContainer style={opacityModalStyle}>
        <TitleContainer>
          <Title semiBold>
            {mode === 'range'
              ? tr('summaries.selectRange')
              : tr('summaries.selectDate')}
          </Title>
        </TitleContainer>
        {renderDatePicker()}
        {renderActionButtons()}
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state: RootState) => ({
  dateRangePickerModal: state.dateRangePickerModal,
});

export default connect(mapStateToProps, null)(DateRangePickerModal);
